// The lobby's landscape preview picture as `WS_RandomGeneratorPreviewer` draws it
// (docs/platform/ClientShell.md "Lobby preview"; crates/sim/src/view/preview.rs): the surface
// `S_RandomGenerationPreview` (ARGB8 1024x512, cleared to (0, 0, 0, 0)) through the previewer's
// camera, a GUI frustum over the bake canvas (world x -4096..4096, y -2048..2048, y up: the
// bake's `mtxVP` into a 1024x512 viewport); the quads of the layers `PPL_RAND_1..3` in submission
// order (the view's order), each `WS_SGNodeTexture::ED_Render`'s `XPRIM_QUAD` (`XPrim_TexShader`:
// the page's texel times the node colour, white) with `m_uRenderFlags | 2`: no 0x80/0x100 outside
// a bake, so `__SetRenderState` 0x1141510 blends `BlendFunc(SRC_ALPHA, ONE_MINUS_SRC_ALPHA)` with
// `BlendEquationSeparate(FUNC_ADD, MAX)` (colour over the cleared black, alpha the maximum). The
// atlas pages are the match's (land.ts `pageTexture`), sampled as the bake samples them (linear,
// clamped, no mipmaps).
import * as THREE from 'three';
import type { SceneView } from '../sim/view.ts';
import { BAKE_FS, BAKE_VS, glTexture, program } from './bake.ts';
import { pageTexture } from './land.ts';

/** `S_RandomGenerationPreview` (CommonData/Scripts/SceneScript.xss). */
export const PREVIEW_W = 1024, PREVIEW_H = 512;

export class PreviewPainter {
  /** The surface: the last picture painted. */
  readonly canvas = document.createElement('canvas');
  private renderer: THREE.WebGLRenderer;
  /** Atlas pages by WTEX path (the pages of the last picture are kept). */
  private pages = new Map<string, Promise<THREE.Texture | null>>();

  constructor() {
    this.canvas.width = PREVIEW_W;
    this.canvas.height = PREVIEW_H;
    // Straight alpha: drawImage composites the surface's alpha as the UI's quad blends it.
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, alpha: true, premultipliedAlpha: false, antialias: false, preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(PREVIEW_W, PREVIEW_H, false);
  }

  /** Loads the scene's pages, then paints it: the page loading and drawing milliseconds. */
  async paint(scene: SceneView): Promise<{ pagesMs: number; drawMs: number }> {
    const t0 = performance.now();
    const used = new Set(scene.quads.map(q => scene.pages[q.page].path));
    for (const [path, t] of this.pages) {
      if (used.has(path)) continue;
      this.pages.delete(path);
      void t.then(x => x?.dispose());
    }
    for (const path of used) if (!this.pages.has(path)) this.pages.set(path, pageTexture(this.renderer, path));
    const pages = await Promise.all(scene.pages.map(p => (used.has(p.path) ? this.pages.get(p.path) ?? null : null)));
    const t1 = performance.now();
    this.draw(scene, pages);
    return { pagesMs: t1 - t0, drawMs: performance.now() - t1 };
  }

  private draw(scene: SceneView, pages: (THREE.Texture | null)[]): void {
    const renderer = this.renderer;
    const gl = renderer.getContext() as WebGL2RenderingContext;
    const glPages = pages.map(t => (t ? glTexture(renderer, t) : null));
    const prog = program(gl, BAKE_VS, BAKE_FS, ['position', 'uv']);
    const sampler = gl.createSampler()!;
    gl.samplerParameteri(sampler, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.samplerParameteri(sampler, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.samplerParameteri(sampler, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.samplerParameteri(sampler, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const vao = gl.createVertexArray()!;
    const vbo = gl.createBuffer()!;
    const ibo = gl.createBuffer()!;
    try {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, PREVIEW_W, PREVIEW_H);
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.CULL_FACE);
      gl.disable(gl.SCISSOR_TEST);
      gl.disable(gl.STENCIL_TEST);
      gl.colorMask(true, true, true, true);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      const quads = scene.quads;
      // BL, TL, TR, BR (`__Platform_BuildQuads`), indices k+2, k+1, k, k+3, k+2, k; consecutive
      // quads of one page share a draw.
      const v = new Float32Array(quads.length * 16);
      const idx = new Uint32Array(quads.length * 6);
      const runs: { page: number; first: number; count: number }[] = [];
      quads.forEach((q, i) => {
        const [l, t, r, b] = q.uv;
        const c = q.corners;
        v.set([c[0], c[1], l, b, c[2], c[3], l, t, c[4], c[5], r, t, c[6], c[7], r, b], i * 16);
        idx.set([4 * i + 2, 4 * i + 1, 4 * i, 4 * i + 3, 4 * i + 2, 4 * i], i * 6);
        const last = runs[runs.length - 1];
        if (last && last.page === q.page) last.count++;
        else runs.push({ page: q.page, first: i, count: 1 });
      });
      gl.bindVertexArray(vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
      gl.bufferData(gl.ARRAY_BUFFER, v, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
      gl.enableVertexAttribArray(1);
      gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);
      gl.useProgram(prog);
      gl.uniform1i(gl.getUniformLocation(prog, 'sTexture'), 0);
      // The previewer's GUI frustum: x / 4096, y / 2048 (`XCamera::CalcProjectionMatrix` 0x123c670).
      gl.uniform4f(gl.getUniformLocation(prog, 'xf'), 2 / 8192, 2 / 4096, 0, 0);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindSampler(0, sampler);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.blendEquationSeparate(gl.FUNC_ADD, gl.MAX);
      for (const r of runs) {
        const page = glPages[r.page];
        if (!page) continue;
        gl.bindTexture(gl.TEXTURE_2D, page);
        gl.drawElements(gl.TRIANGLES, r.count * 6, gl.UNSIGNED_INT, r.first * 24);
      }
    } finally {
      gl.disable(gl.BLEND);
      gl.bindVertexArray(null);
      gl.bindSampler(0, null);
      gl.deleteVertexArray(vao);
      gl.deleteBuffer(vbo);
      gl.deleteBuffer(ibo);
      gl.deleteSampler(sampler);
      gl.deleteProgram(prog);
      renderer.resetState();
    }
  }

  /** Releases the pages and the GL context. */
  dispose(): void {
    for (const t of this.pages.values()) void t.then(x => x?.dispose());
    this.pages.clear();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }
}
